// Sim -> UI bridge. The sim pushes a lightweight snapshot into signals,
// throttled to at most 10 updates per second. UI sends commands back via `dispatch`.

import type { Placement } from "@degen/sim/houses/houses";
import { canCheckIn } from "@degen/sim/gas";
import { signal } from "@preact/signals";
import type { Command, CommandResult, Engine } from "@degen/sim/engine";
import type { Clock, Speed } from "@degen/sim/clock";
import { dayOf, formatClock } from "@degen/sim/state";
import { hasKolBadge, rankName } from "@degen/sim/rank";
import { netWorth } from "@degen/sim/player";
import type { AvatarIdentity } from "@degen/sim/avatar/traits";
import type { Zone } from "../world/districts";
import type { ChoiceEvent } from "@degen/sim/events/sleuth";
import type { RektInfo } from "@degen/sim/rekt";
import { currentQuest, QUESTS } from "@degen/sim/tutorial";
import { unreadCount } from "@degen/sim/messages/messages";
import type { GameState } from "@degen/sim/state";
import { play, type Sfx } from "./sound";

export interface HudSnapshot {
  name: string;
  handle: string;
  pfpSeed: number;
  pfpTraits: Record<string, string>;
  avatar: AvatarIdentity;
  cash: number;
  dgn: number;
  checkInReady: boolean;
  phone: string; // phone model in your hand (drives the phone skin)
  equipped: { frame?: string; outfit?: string; phoneSkin?: string };
  netWorth: number;
  followers: number;
  trust: number;
  heat: number;
  rank: string;
  kolBadge: boolean;
  day: number;
  clock: string;
  speed: Speed;
  paused: boolean;
  /** First pending choice event (popup), if any. */
  event: ChoiceEvent | null;
  rekt: RektInfo | null;
  quest: string | null;
  questId: string | null;
  questStep: number;
  questTotal: number;
  unread: number;
}

export const hud = signal<HudSnapshot | null>(null);
export const phoneOpen = signal(false);
export const activeApp = signal<string | null>(null);
/** Per-app navigation params, e.g. { token: "t12" } for Pumpr or { compose: "alpha", token } for Chirp. */
export const appParams = signal<Record<string, string>>({});
/** Bumps ~2x per second so app screens refresh without re-rendering at frame rate. */
export const appTick = signal(0);

/** Phone minimized to a pill (it remembers the open app). */
export const phoneMin = signal(false);

export function openApp(id: string, params: Record<string, string> = {}) {
  appParams.value = params;
  activeApp.value = id;
  phoneMin.value = false;
  phoneOpen.value = true;
}

export function minimizePhone() {
  phoneOpen.value = false;
  phoneMin.value = activeApp.value !== null;
}

/** P / FAB: open the phone, or minimize it if it's open. */
export function togglePhone() {
  if (phoneOpen.value) minimizePhone();
  else {
    phoneMin.value = false;
    phoneOpen.value = true;
  }
}

/** On-screen run toggle (phones); desktop uses Shift. */
export const moveMode = { run: false };
export const runOn = signal(false);
export const toast = signal<string | null>(null);
/** The walk-in zone the player is standing in (Trading Floor, Home, Café...). */
export const zone = signal<Zone | null>(null);
/** Where the player is (for the minimap / map). Updated ~10x per second by the game loop. */
export interface PlayerLoc {
  x: number;
  z: number;
  inside: string | null;
  driving: boolean;
  heading: number;
  car: { x: number; z: number } | null;
  highway: { name: string; km: number } | null;
  kmh: number;
  riders: { id: string; handle: string }[];
  seats: number;
  riding: { id: string; handle: string } | null;
  carModel: string | null;
  carColor: string | null;
}
export const playerLoc = signal<PlayerLoc>({
  x: 0, z: 8, inside: null, driving: false, heading: 0, car: null, highway: null, kmh: 0, riders: [], seats: 0, riding: null, carModel: null, carColor: null,
});
/** On-screen pedals write here; the world reads it every frame while driving. */
export const driveInput = { throttle: 0, steer: 0 };
export const mapOpen = signal(false);
/** True while playing in a shared world (server clock: no speed controls). */
export const sharedMode = signal(false);

/** Account id of the person you tapped in the world (shows their card). */
export const personCard = signal<string | null>(null);

/** Render-side controls the UI can call (set by main once the 3D world exists). */
export interface WorldControls {
  enter(id: string): void;
  exit(): void;
  walkTo(x: number, z: number): void;
  drive(): void;
  park(): void;
  testDrive(modelId: string): void;
  enterHighway(): void;
  leaveHighway(): void;
  dropOff(accountId?: string): void;
  /** Walk into another player's published apartment (view-only). */
  visitHouse(houseId: string, layout: Placement[], handle: string): void;
}
let worldRef: WorldControls | null = null;
export function registerWorld(w: WorldControls) {
  worldRef = w;
}
export function world(): WorldControls | null {
  return worldRef;
}

/** The how-to-play screens: the one-time welcome card, or the Help sheet. */
export const helpOpen = signal<false | "welcome" | "help">(false);

/** Pause / resume the game clock for a UI reason (e.g. while reading Help). */
export function holdClock(reason: string, on: boolean) {
  if (!clockRef) return;
  if (on) clockRef.hold(reason);
  else clockRef.release(reason);
}

/** Full-screen rank-up card (rank index), or null. */
export const rankUp = signal<number | null>(null);

let engineRef: Engine | null = null;
let clockRef: Clock | null = null;
let lastPush = 0;
let lastAppTick = 0;
let lastPending = 0;

/** Read-only access to the live game state for apps that need more than the HUD snapshot. */
export function gameState() {
  return engineRef?.state ?? null;
}

export function connectBridge(engine: Engine, clock: Clock) {
  engineRef = engine;
  clockRef = clock;
  engine.on((e) => {
    if (e.type === "rankChange" && e.to > e.from) {
      rankUp.value = e.to;
      play("rankup");
    }
    if (e.type === "rekt") {
      play("rekt");
      try {
        navigator.vibrate?.([200, 100, 400]);
      } catch {
        /* unsupported */
      }
    }
  });
  pushSnapshot(true);
}

export function pushSnapshot(force = false) {
  if (!engineRef || !clockRef) return;
  const now = performance.now();
  if (!force && now - lastPush < 100) return;
  lastPush = now;
  if (force || now - lastAppTick > 500) {
    lastAppTick = now;
    appTick.value++;
  }
  const s = engineRef.state;
  const p = s.player;
  hud.value = {
    name: p.name,
    handle: p.handle,
    pfpSeed: p.pfp.seed,
    pfpTraits: p.pfp.traits,
    avatar: p.avatar,
    cash: p.cash,
    dgn: p.dgn,
    phone: p.phone,
    equipped: { ...s.progress.equipped },
    checkInReady: canCheckIn(s),
    netWorth: netWorth(s),
    followers: p.followers,
    trust: p.trust,
    heat: p.heat,
    rank: rankName(p.rank),
    kolBadge: hasKolBadge(p.followers, p.trust),
    day: dayOf(s.tick),
    clock: formatClock(s.tick),
    speed: clockRef.speed,
    paused: clockRef.paused,
    event: s.events.pending[0] ?? null,
    rekt: s.rekt,
    quest: currentQuest(p.tutorial)?.text ?? null,
    questId: currentQuest(p.tutorial)?.id ?? null,
    questStep: p.tutorial.step,
    questTotal: QUESTS[p.tutorial.track].length,
    unread: unreadCount(s),
  };
  // Hold the clock while a popup or the rekt screen needs the player.
  if (s.events.pending.length > lastPending) play("popup");
  lastPending = s.events.pending.length;
  if (s.events.pending.length) clockRef.hold("event");
  else clockRef.release("event");
  if (s.rekt) clockRef.hold("rekt");
  else clockRef.release("rekt");
  notifyFromLog(s);
}

const CMD_SFX: Partial<Record<Command["type"], Sfx>> = {
  BUY_TOKEN: "buy", SELL_TOKEN: "sell", POST: "post", MINT_NFT: "coin", FARM_TASK: "coin", LAUNCH_COIN: "coin", LAUNCH_COLLECTION: "coin", LAUNCH_AIRDROP: "coin",
};

/** Shared worlds route market actions to the server (returns null to let the local sim handle it). */
let dispatchHook: ((cmd: Command) => CommandResult | null) | null = null;
export function setDispatchHook(h: typeof dispatchHook) {
  dispatchHook = h;
}
/** Called after every successful player command (apartment publishing, etc). */
let afterCommand: ((cmd: Command) => void) | null = null;
export function setAfterCommand(f: typeof afterCommand) {
  afterCommand = f;
}

/** Player actions today (telemetry: actions per day). */
export let actionsToday = 0;
export function resetActions() {
  actionsToday = 0;
}

export function dispatch(cmd: Command): CommandResult {
  if (!engineRef) return { ok: false, error: "Game not loaded." };
  actionsToday++;
  const hooked = dispatchHook?.(cmd);
  if (hooked) {
    if (!hooked.ok) {
      showToast(hooked.error);
      play("error");
    }
    pushSnapshot(true);
    return hooked;
  }
  const r = engineRef.dispatch(cmd);
  if (!r.ok) {
    showToast(r.error);
    play("error");
  } else {
    if (CMD_SFX[cmd.type]) play(CMD_SFX[cmd.type]!);
    afterCommand?.(cmd);
  }
  // Haptics for big wins (spec 13.4).
  if (r.ok && cmd.type === "SELL_TOKEN" && (r.data?.pnl ?? 0) > 1_000) {
    try {
      navigator.vibrate?.([40, 30, 40, 30, 120]);
    } catch {
      /* unsupported */
    }
  }
  pushSnapshot(true);
  return r;
}

export function setSpeed(speed: Speed) {
  if (sharedMode.value) return; // shared worlds run on the server clock
  if (!clockRef) return;
  clockRef.speed = speed;
  pushSnapshot(true);
}

// Toasts queue up so a burst of events doesn't overwrite itself.
const toastQueue: string[] = [];
let toastTimer: ReturnType<typeof setTimeout> | undefined;
function nextToast() {
  toast.value = toastQueue.shift() ?? null;
  if (toast.value) toastTimer = setTimeout(nextToast, Math.min(5000, 1800 + toast.value.length * 30));
}
export function showToast(msg: string) {
  if (toastQueue.length > 4) toastQueue.shift();
  toastQueue.push(msg);
  if (!toast.value) {
    clearTimeout(toastTimer);
    nextToast();
  }
}

// Log entries the player should hear about right away.
const NOTIFY = new Set([
  "held_rug", "tge", "sybil_flagged", "season2", "exploit", "exposed", "sleuth_cleared", "call_right", "call_wrong",
  "viral", "drained", "scam_lesson", "presale_won", "presale_lost", "pet_left", "pet_adopted", "nft_sold", "abandoned", "collab_rug", "player_rug", "quest", "trust", "beef", "comeback", "rank_down",
]);
let logSeen = -1;
function notifyFromLog(s: GameState) {
  if (logSeen < 0) {
    logSeen = s.logSeq; // don't replay old history after a load
    return;
  }
  for (const e of s.log) {
    if (e.seq < logSeen) continue;
    if (!NOTIFY.has(e.kind)) continue;
    if (e.kind === "exploit" && !e.data?.mine) continue;
    if (e.kind === "season2" && !s.airdrops.protocols.some((p) => p.id === e.data?.protocol && p.playerPoints > 0)) continue;
    if (e.kind === "tge" && !e.data?.mine) continue;
    showToast(e.text);
  }
  logSeen = s.logSeq;
}
